import React, { useContext, useMemo, useState, useEffect } from "react";
import {
  HolderOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Table, Tag, Modal } from "antd";
import { useQaQuestions } from "./store";
import { isArray } from "lodash";
import QA_EditQuestion from "./QA_EditQuestion";
import { isString } from "lodash";
import QA_AddQuestion from "./QA_AddQuestion";

const RowContext = React.createContext({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{
        cursor: "move",
      }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const columns = (onEdit, onDelete) => [
  {
    key: "sort",
    align: "center",
    width: 80,
    render: () => <DragHandle />,
  },
  {
    title: "Question Title",
    dataIndex: "question_title",
    render: (text) => <span>{text === null ? "-" : text}</span>,
  },
  {
    title: "Parameter Type",
    dataIndex: "parameter_type",
    render: (text) => <span>{text === null ? "-" : text}</span>,
  },
  {
    title: "Critical",
    dataIndex: "is_critical_question",
    align: "center",
    render: (text, record) => (
      <Tag color={record.is_critical_question ? "red" : "blue"}>
        <span>{record.is_critical_question ? "YES" : "NO"}</span>
      </Tag>
    ),
  },
  {
    title: "Question Score",
    dataIndex: "question_score",
    align: "center",
    render: (text) => (
      <Tag color="green">
        <span>{text}</span>
      </Tag>
    ),
  },
  {
    title: "Actions",
    key: "actions",
    render: (text, record) => (
      <>
        <Button
          icon={<EditOutlined />}
          onClick={() => onEdit(record)}
          style={{ marginRight: 8 }}
        />
        <Button
          icon={<DeleteOutlined />}
          onClick={() => onDelete(record._id)}
          danger
        />
      </>
    ),
  },
];

const Row = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
        }
      : {}),
  };

  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const QA_Table = ({
  savedFormValues,
  isReordering,
  setIsReordering,
  showSubmitCancel,
  setShowSubmitCancel,
  setTriggerFetch,
  isAdding,
  setIsAdding,
}) => {
  const [dataSource, setDataSource] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleTableChange = (pagination) => {
    setPageSize(pagination?.pageSize);
    setCurrentPage(pagination?.current);
  };

  const {
    questionsData,
    questionsDataLoading,
    fetchReorderQuestion,
    fetchDeleteQuestion,
    fetchEditQuestion,
  } = useQaQuestions();

  useEffect(() => {
    setDataSource(questionsData);
  }, [questionsData]);
  
//Total score
const totalScore = useMemo(() => 
  dataSource.reduce((sum, item) => sum + item.question_score, 0),
  [dataSource]
);

  const queryParamData = {
    ...(isArray(savedFormValues.monthCount)
      ? undefined
      : { monthCount: savedFormValues.monthCount }),
    ...(isArray(savedFormValues.engagementType)
      ? undefined
      : { engagementType: savedFormValues.engagementType }),
  };

  const onDragEnd = ({ active, over }) => {
    if (!isReordering || !active || !over) return;
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record._id === active.id
        );
        const overIndex = prevState.findIndex(
          (record) => record._id === over.id
        );
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
  };

  const handleEdit = (record) => {
    setEditingQuestion({
      ...record,
      is_mandatory: record.is_mandatory ? "Yes" : "No",
      is_multiple_select_option: record.is_multiple_select_option
        ? "Yes"
        : "No",
      is_critical_question: record.is_critical_question ? "Yes" : "No",
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this question?",
      onOk: async () => {
        const data = { ...queryParamData };
        await fetchDeleteQuestion(id, data);
        setTriggerFetch(true);
      },
    });
  };

  const handleSubmit = async () => {
    const reorderedIds = dataSource.map((item) => item._id);
    const data = {
      scoringFormQuestions: reorderedIds,
      ...queryParamData,
    };
    await fetchReorderQuestion(data);
    setTriggerFetch(true);
    setIsReordering(false);
    setShowSubmitCancel(false);
  };

  const handleCancel = () => {
    setIsReordering(false);
    setShowSubmitCancel(false);
  };

  const processEditedQuestion = (editingQuestion) => {
    if (!editingQuestion) return null;
    const correctAnswersArray = editingQuestion?.correct_answers
      ? isString(editingQuestion.correct_answers)
        ? editingQuestion.correct_answers
            .split(",")
            .map((answer) => answer.trim())
            .filter((answer) => answer)
        : editingQuestion.correct_answers
      : [];

    return {
      ...editingQuestion,
      question_score:Number(editingQuestion?.question_score),
      correct_answers: correctAnswersArray,
      serial_number: Number(editingQuestion?.serial_number),
      section: Number(editingQuestion?.section),
      is_mandatory: editingQuestion?.is_mandatory === "Yes",
      is_critical_question: editingQuestion?.is_critical_question === "Yes",
      is_multiple_select_option:
        editingQuestion?.is_multiple_select_option === "Yes",
    };
  };

  const handleModalOk = () => {
    const processedQuestion = processEditedQuestion(editingQuestion);
    if (!processedQuestion || !processedQuestion._id) return;
  // Calculate current total score excluding the current question being edited
  const currentTotalScore = dataSource
    .filter(item => item._id !== processedQuestion._id)
    .reduce((sum, item) => sum + item.question_score, 0);

  // Add the new/edited question's score
  const newTotalScore = currentTotalScore + processedQuestion.question_score;

  // Check if total score exceeds 100
  if (newTotalScore > 100) {
    Modal.error({
      title: 'Score Limit Exceeded',
      content: 'Total question score cannot exceed 100'
    });
    return;
  }
    fetchEditQuestion(processedQuestion, processedQuestion._id);
    setTriggerFetch(true);
    setIsModalVisible(false);
    setEditingQuestion(null);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingQuestion(null);
  };

  return (
    <>
      <div className="w-100" style={{ minHeight: "80vh" }}>
        {showSubmitCancel && (
          <div
            className="d-flex justify-content-end"
            style={{ marginBottom: 24 }}
          >
            <Button
              type="primary"
              onClick={handleSubmit}
              style={{ marginRight: 8 }}
            >
              Submit
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </div>
        )}
        <DndContext onDragEnd={onDragEnd} modifiers={[restrictToVerticalAxis]}>
          <SortableContext
            items={dataSource.map((item) => item._id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              className="my-custom-table"
              style={{ position: "relative", zIndex: 1 }}
            >
           {(dataSource.length > 0 && !showSubmitCancel) && (
              <div className="d-flex justify-content-end mb-4">
                <Tag>
                  <div className="p-2 bold fw-bold">Total Score: {totalScore}</div>
                </Tag>
              </div>
            )}
              <Table
                components={{
                  body: {
                    row: Row,
                  },
                }}
                rowKey="_id"
                dataSource={dataSource}
                columns={columns(handleEdit, handleDelete)}
                loading={questionsDataLoading}
                onChange={handleTableChange}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: dataSource?.length,
                  showSizeChanger: true,
                }}
              />
            </div>
          </SortableContext>
        </DndContext>
        <Modal
          title="Edit Question"
          visible={isModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
        >
          <QA_EditQuestion
            editingQuestion={editingQuestion}
            setEditingQuestion={setEditingQuestion}
          />
        </Modal>
        <Modal
          title="Add Question"
          visible={isAdding}
          onCancel={() => setIsAdding(false)}
          footer={null}
        >
          <QA_AddQuestion
            setIsAdding={setIsAdding}
            savedFormValues={savedFormValues}
            setTriggerFetch={setTriggerFetch}
            totalScore={totalScore}
          />
        </Modal>
      </div>
    </>
  );
};

export default QA_Table;
